"use client"
import { useEffect, useState } from "react"
import moment from "moment"
import { useNavigate, useSearchParams } from "react-router-dom"
import { RiDeleteBin6Fill } from "react-icons/ri"
import { FiSearch } from "react-icons/fi"
import { useDispatch, useSelector } from "react-redux"
import { fetchApplications, updateApplication } from "../../../../redux/features/applicationSlice"
import LimitDropdown from '../../../common/LimitDropdown'
import DateSelector from "../../../admin/HomePage/DateSelector"

// Define status filter options
const statusFilters = [
  { label: "All", value: "All" },
  { label: "In-Progress", value: "In-Progress" },
  { label: "Submitted", value: "Submitted" },
  { label: "Finalized", value: "Finalized" },
  { label: "Withdrawn", value: "WithdrawnByClient" }
]


export default function Applications() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const dispatch = useDispatch()
  const { applications, isLoading } = useSelector((state) => state.application)
  const applicationsList = Array.isArray(applications)
    ? applications
    : Array.isArray(applications?.applications)
      ? applications.applications
      : []

  const [selectedApp, setSelectedApp] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("")
  const [selectedStatus, setSelectedStatus] = useState("All")
  // pagination state
  const initialPageFromUrl = Number(searchParams.get("page"))
  const [page, setPage] = useState(Number.isInteger(initialPageFromUrl) && initialPageFromUrl > 0 ? initialPageFromUrl : 1)
  const [limit, setLimit] = useState(10)

  // Date filter states
  const [fromMonth, setFromMonth] = useState(null)
  const [fromYear, setFromYear] = useState(null)
  const [toMonth, setToMonth] = useState(null)
  const [toYear, setToYear] = useState(null)

  const hasFromDate = Boolean(fromMonth && fromYear)
  const hasToDate = Boolean(toMonth && toYear)
  const fromDateStr = hasFromDate ? `${fromYear}-${String(fromMonth).padStart(2, "0")}-01` : null
  const toDateStr = hasToDate
    ? (() => {
        const endDay = moment(`${toYear}-${toMonth}`, "YYYY-M").daysInMonth()
        return `${toYear}-${String(toMonth).padStart(2, "0")}-${String(endDay).padStart(2, "0")}`
      })()
    : null

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage((prevPage) => (prevPage === 1 ? prevPage : 1))
      setDebouncedSearchTerm(searchTerm)
    }, 600)

    return () => clearTimeout(timer)
  }, [searchTerm])

  useEffect(() => {
    const filters = {}

    if (fromDateStr) filters.from = fromDateStr
    if (toDateStr) filters.to = toDateStr
    if (selectedStatus !== "All") filters.status = selectedStatus
    if (debouncedSearchTerm.trim()) filters.searchString = debouncedSearchTerm.trim()

    dispatch(fetchApplications({
      page,
      limit,
      ...(Object.keys(filters).length > 0 ? { filters } : {}),
    }))
  }, [dispatch, page, limit, fromDateStr, toDateStr, selectedStatus, debouncedSearchTerm])

  const handleViewApplication = (id) => {
    navigate(`/client/applications/form/${id}`)
  }

  const handleDeleteClick = (app) => {
    setSelectedApp(app)
    setShowModal(true)
  }

  const confirmDelete = async () => {
    try {
      setLoading(true)
      // Client-initiated withdraw => send canonical status
      await dispatch(updateApplication({ id: selectedApp._id, formData: { status: "WithdrawnByClient" } }))
      setShowModal(false)
      setSelectedApp(null)
    } catch (err) {
      console.log(err)
    }
    setLoading(false)
  }

  const handleDateChange = (type, value) => {
    switch (type) {
      case "fromMonth":
        setFromMonth(value)
        setPage(1)
        break
      case "fromYear":
        setFromYear(value)
        setPage(1)
        break
      case "toMonth":
        setToMonth(value)
        setPage(1)
        break
      case "toYear":
        setToYear(value)
        setPage(1)
        break
      default:
        break
    }
  }

  const CustomDateSelector = () => (
    <DateSelector
      fromMonth={fromMonth}
      setFromMonth={(value) => handleDateChange("fromMonth", value)}
      fromYear={fromYear}
      setFromYear={(value) => handleDateChange("fromYear", value)}
      toMonth={toMonth}
      setToMonth={(value) => handleDateChange("toMonth", value)}
      toYear={toYear}
      setToYear={(value) => handleDateChange("toYear", value)}
    />
  )

  // Use server-provided pagination metadata when available
  const totalPagesFromServer = useSelector((state) => state.application.totalPages) || 1
  const currentPageFromServer = useSelector((state) => state.application.currentPage) || 1
  const safePage = Math.min(Math.max(page, 1), totalPagesFromServer)
  const paginated = applicationsList
  const hasMultiplePages = totalPagesFromServer > 1

  useEffect(() => {
    if (page !== currentPageFromServer) {
      // keep local page in sync with server if server provided a different currentPage
      setPage(currentPageFromServer)
    }
  }, [currentPageFromServer])

  useEffect(() => {
    if (page > totalPagesFromServer) {
      setPage(totalPagesFromServer)
    }
  }, [page, totalPagesFromServer])

  useEffect(() => {
    const params = new URLSearchParams(searchParams)

    if (safePage > 1) {
      params.set("page", String(safePage))
    } else {
      params.delete("page")
    }

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true })
    }
  }, [safePage, searchParams, setSearchParams])

  const handlePageChange = (newPage) => {
    const target = Math.min(Math.max(newPage, 1), totalPagesFromServer)
    if (target !== page) setPage(target)
  }

  return (
    <div className="lg:p-6">
      <h1 className="text-2xl font-semibold mb-6">All Applications</h1>

      {/* Filters Section */}
      <div className="w-full mb-5">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:flex xl:flex-nowrap xl:flex-1 xl:items-end xl:gap-2">
            <div className="relative w-full xl:w-[260px] xl:flex-none">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or ID"
                className="w-full rounded border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-[#005787]"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                }}
              />
            </div>
          </div>

          <div className="flex justify-end xl:justify-start xl:flex-none xl:min-w-[260px]">
            <div className="w-full md:max-w-[400px] xl:max-w-none xl:w-auto">
              <CustomDateSelector />
            </div>
          </div>
          
        </div>
      </div>

      {/* Status Filter */}
      <div className="flex justify-start items-center md:mb-5">
        {/* Status Tabs for Large Screens */}
        <div className="hidden md:flex flex-wrap gap-3 items-center">
          {statusFilters.map((s) => (
            <h2
              key={s.value}
              onClick={() => {
                setSelectedStatus(s.value)
                setPage(1)
              }}
              className={`cursor-pointer text-sm pt-0.5 ${selectedStatus === s.value ? "border-b-[3px] border-b-black" : "text-[#B3B0B0]"
                }`}
            >
              {s.label}
            </h2>
          ))}

        </div>
      </div>

      {/* Dropdown for Status on Small Screens */}
      <div className="md:hidden mt-4 mb-4">
        <select
          value={selectedStatus}
          onChange={(e) => {
            setSelectedStatus(e.target.value)
            setPage(1)
          }}
          className="w-full p-2 border rounded-md text-gray-700 text-sm"
        >
          {statusFilters.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

      </div>

      <div className="hidden md:grid grid-cols-7 text-sm font-medium text-gray-900 mb-4">
        <div className="pl-1 inline-flex">
          <span className="hidden xl:flex">Application</span> Type
        </div>
        <div className="pl-3">App Date</div>
        <div className="pl-1">Phone No.</div>
        <div className="pl-2 whitespace-normal break-words">From</div>
        <div className="pl-1">Status</div>
        <div className="pl-1 whitespace-normal break-words">Country</div>
        <div className="pl-1">Action</div>
      </div>

      <div className="space-y-6">
        {isLoading && !loading ? (
          <div className="flex items-center justify-center min-h-[300px]">
            <div className="w-10 h-10 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : paginated && paginated.length > 0 ? (
          paginated.map((app, index) => (
            <div key={app._id} className="space-y-3">
              <div className={`${index % 2 === 0 ? "bg-[#005787]" : "bg-[#036494]"} text-white rounded-lg p-4`}>
                <div className="grid grid-cols-1 md:grid-cols-7 gap-4 items-center">
                  {/* Mobile View - Headings Included Inside Each Application */}
                  <div className="md:hidden flex flex-row gap-3">
                    <p className="text-sm font-semibold">Application Type:</p>
                    <p>{app.visaCategory}</p>
                  </div>
                  <div className="md:hidden flex flex-row gap-3">
                    <p className="text-sm font-semibold">App Date:</p>
                    <p>{moment(app.createdAt).format("DD/MM/YY")}</p>
                  </div>
                  <div className="md:hidden flex flex-row gap-3">
                    <p className="text-sm font-semibold">Phone No.:</p>
                    <p>{app.mobileNumber}</p>
                  </div>
                  <div className="md:hidden flex flex-row gap-3 min-w-0">
                    <p className="text-sm font-semibold">From:</p>
                    <p className="break-words max-w-[200px]">{app.country}</p>
                  </div>
                  <div className="md:hidden flex flex-row gap-3">
                    <p className="text-sm font-semibold">Status:</p>
                    <p>{app.status === "WithdrawnByClient" ? <>
                      Withdrawn
                    </> : <>
                      {app.status}
                    </>}</p>
                  </div>
                  <div className="md:hidden flex flex-row gap-3 min-w-0">
                    <p className="text-sm font-semibold">Country:</p>
                    <p className="break-words">{app.countryOfInterest}</p>
                  </div>

                  {/* Desktop View - Normal Layout */}
                  <div className="hidden md:block">{app.visaCategory}</div>
                  <div className="hidden md:block">{moment(app.createdAt).format("DD/MM/YY")}</div>
                  <div className="hidden md:block">{app.mobileNumber}</div>
                  <div className="hidden md:block min-w-0 break-words">{app.country}</div>
                  <div className="hidden md:block"> {app?.status === "WithdrawnByClient" || app?.status === "Cancelled"
                    ? "Cancelled"
                    : app?.status === "WithdrawnByAgent"
                      ? "Withdrawn"
                      : app?.status || "N/A"} </div>
                  <div className="hidden md:block min-w-0 break-words">{app.countryOfInterest}</div>

                  <div className="flex flex-row items-center justify-between gap-2 w-full">
                    <button
                      onClick={() => handleViewApplication(app._id)}
                      className="bg-white text-[#036494] px-6 py-2 rounded-full text-sm font-medium hover:bg-gray-100 transition"
                    >
                      View
                    </button>

                    {app.status === "Finalized" || app.status === "Submitted"  || app.status === "WithdrawnByClient" || app.status === "WithdrawnByAgent" || app.status === "Cancelled" || app.status === "VisaApplied" || app.status === "WithdrawnByClient" || app.status === "WithdrawnByAgent" || app.status === "Cancelled" || app.status === "In-Progress" ? null : (
                      <button
                        onClick={() => handleDeleteClick(app)}
                        className="bg-white rounded-full h-8 w-8 flex items-center justify-center hover:bg-red-100 transition"
                      >
                        <RiDeleteBin6Fill className="h-5 w-5 text-red-600" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="text-center text-red-500 pt-10">No applications found matching your filters.</p>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-6 flex items-center justify-center gap-2">
        {hasMultiplePages ? (
          <>
            <button
              onClick={() => handlePageChange(Math.max(safePage - 1, 1))}
              disabled={safePage === 1 || isLoading}
              className={`px-2 md:px-3 py-2 rounded-md ${safePage === 1 || isLoading
                  ? "bg-gray-200/50 text-gray-400 cursor-default"
                  : "bg-gray-100 text-[#005787] hover:bg-gray-300"
                }`}
            >
              &lt;
            </button>

            {Array.from({ length: totalPagesFromServer }).map((_, index) => {
              const pageNumber = index + 1
              if (
                pageNumber === 1 ||
                pageNumber === totalPagesFromServer ||
                (pageNumber >= safePage - 1 && pageNumber <= safePage + 1)
              ) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={`px-3 md:px-4 py-1.5 md:py-2 rounded-md border ${safePage === pageNumber
                        ? "bg-[#005787] text-white border-[#334155]"
                        : "bg-gray-100 text-[#005787] hover:bg-gray-300 border-gray-200"
                      }`}
                  >
                    {pageNumber}
                  </button>
                )
              }
              if (pageNumber === safePage - 2 || pageNumber === safePage + 2) {
                return <span key={pageNumber} className="px-2 py-1 text-gray-500">...</span>
              }
              return null
            })}

            <button
              onClick={() => handlePageChange(Math.min(safePage + 1, totalPagesFromServer))}
              disabled={safePage === totalPagesFromServer || isLoading}
              className={`px-2 md:px-3 py-2 rounded-md ${safePage === totalPagesFromServer || isLoading
                  ? "bg-gray-200/50 text-gray-400 cursor-default"
                  : "bg-gray-100 text-[#005787] hover:bg-gray-300"
                }`}
            >
              &gt;
            </button>
          </>
        ) : null}

        <div className="flex items-center ml-4">
          <LimitDropdown value={limit} onChange={(v) => { setLimit(v); setPage(1); }} />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showModal && selectedApp && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-5 md:p-6 rounded-lg shadow-lg mx-2">
            <h2 className="md:text-lg font-normal mb-4">
              Are you sure you want to delete the <span className="font-semibold">{selectedApp.visaCategory}</span>{" "}
              application for <span className="font-semibold">{selectedApp.countryOfInterest}</span>?
            </h2>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="text-sm md:text-base px-4 py-2 rounded-full bg-[#005887ea] text-white"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="w-[88.5px] h-[36px] md:w-[96px] md:h-[40px] text-sm md:text-base px-4 py-2 bg-red-600 text-white rounded-full flex items-center justify-center"
              >
                {loading ? <span className="loader"></span> : <span>Confirm</span>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
